/**
 * PlanetMovementSettings - Configuration panel for Planet Drag navigation
 * Similar to TimeMotionSettings but for degree-based movement
 * With a button to open the navigation overlay
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  HStack,
  Text,
  Badge,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { devLog } from '../../../../utils/devLog';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../utils/localStorageUtils';
import { ExpandableLabel } from '../DataSections/ExpandableLabel';
import { MotionOpenButton } from '../DataSections/components/MotionOpenButton';
import { globalEventStream, createEvent } from '../universal_architecture/core/EventStream';
import type { BaseEvent, EventSubscription } from '../universal_architecture/types/EventTypes';

const DEBUG = true;

// Step values in degrees (x10 increments)
const STEP_VALUES = [0.001, 0.01, 0.1, 1, 10];
const DEFAULT_STEP = 1;

interface PlanetMovementSettingsProps {
  chartId: string;
  planet: string; // Lg, Sy, Ch
  disabled?: boolean;
}

/**
 * Format step value for display
 */
const formatStep = (val: number): string => {
  if (val >= 1) return val.toString();
  if (val >= 0.1) return val.toFixed(1);
  if (val >= 0.01) return val.toFixed(2);
  return val.toFixed(3);
};

/**
 * Calculate range description
 * API: degree_range (360 points) × degree_step = actual degrees
 */
const formatRange = (stepDegrees: number): string => {
  const range = Math.round(360 * stepDegrees * 1000) / 1000;
  return `${range}°`;
};

/**
 * StepValueSelector - Inline badge selector for step value (no popover)
 */
const StepValueSelector: React.FC<{
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
  return (
    <Wrap spacing={1}>
      {STEP_VALUES.map(stepVal => (
        <WrapItem key={stepVal}>
          <Badge
            colorScheme={stepVal === value ? 'purple' : 'gray'}
            variant={stepVal === value ? 'solid' : 'outline'}
            fontSize="xs"
            px={1.5}
            py={0.5}
            borderRadius="sm"
            cursor={disabled ? 'not-allowed' : 'pointer'}
            opacity={disabled ? 0.5 : 1}
            onClick={() => !disabled && onChange(stepVal)}
            _hover={disabled ? {} : {
              bg: stepVal === value ? undefined : 'gray.100',
              transform: 'scale(1.02)'
            }}
            transition="all 0.15s"
          >
            {formatStep(stepVal)}°
          </Badge>
        </WrapItem>
      ))}
    </Wrap>
  );
};

/**
 * PlanetMovementSettings component
 */
export const PlanetMovementSettings: React.FC<PlanetMovementSettingsProps> = ({
  chartId,
  planet,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeMotionType, setActiveMotionType] = useState<'planet-drag' | 'time-motion' | null>(null);
  const [activePlanet, setActivePlanet] = useState<string | null>(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Load settings from localStorage
  const getStoredStep = useCallback(() => {
    const settings = getLocalStorageItem<Record<string, any> | null>(`chart-settings-${chartId}`, null);
    return settings?.movementStepDegrees?.[planet] ?? DEFAULT_STEP;
  }, [chartId, planet]);

  const [stepValue, setStepValue] = useState<number>(() => getStoredStep());

  // Save settings to localStorage
  const saveSettings = useCallback((value: number) => {
    const settingsKey = `chart-settings-${chartId}`;
    const settings = getLocalStorageItem<Record<string, any>>(settingsKey, {});

    // Update movementStepDegrees
    const movementStepDegrees = settings.movementStepDegrees || { Lg: 1, Sy: 1, Ch: 1 };
    movementStepDegrees[planet] = value;
    settings.movementStepDegrees = movementStepDegrees;

    setLocalStorageItem(settingsKey, settings);
  }, [chartId, planet]);

  // Handle step value change
  const handleValueChange = useCallback((value: number) => {
    setStepValue(value);
    saveSettings(value);

    // Emit settings changed event (for overlay to update if open)
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.movement.step.changed',
      'PlanetMovementSettings',
      {
        metadata: {
          chartId,
          planet,
          stepDegrees: value,
        },
      }
    ));
  }, [chartId, planet, saveSettings]);

  // Handle open button click
  const handleOpenClick = useCallback(() => {
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.gesture.start',
      'PlanetMovementSettings',
      {
        metadata: {
          chartId,
          planet,
          stepDegrees: stepValue,
        },
      }
    ));
  }, [chartId, planet, stepValue]);

  // Listen for all motion overlay open/close events
  useEffect(() => {
    if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] Subscribing to events`);
    const subscriptions: EventSubscription[] = [];

    // Planet drag events
    subscriptions.push(
      globalEventStream.subscribe('planet.drag.overlay.opened', (event) => {
        if (event.metadata?.chartId === chartId) {
          const openedPlanet = event.metadata?.planet as string;
          if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] planet.drag.overlay.opened: ${openedPlanet}`);
          setActiveMotionType('planet-drag');
          setActivePlanet(openedPlanet);
          if (openedPlanet === planet) {
            setIsOverlayOpen(true);
          }
        }
      })
    );

    subscriptions.push(
      globalEventStream.subscribe('planet.drag.overlay.closed', (event) => {
        if (event.metadata?.chartId === chartId) {
          if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] planet.drag.overlay.closed`);
          setActiveMotionType(null);
          setActivePlanet(null);
          setIsOverlayOpen(false);
        }
      })
    );

    // Time motion events
    subscriptions.push(
      globalEventStream.subscribe('time.motion.overlay.opened', (event) => {
        if (event.metadata?.chartId === chartId) {
          if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] time.motion.overlay.opened`);
          setActiveMotionType('time-motion');
          setActivePlanet(null);
        }
      })
    );

    subscriptions.push(
      globalEventStream.subscribe('time.motion.overlay.closed', (event) => {
        if (event.metadata?.chartId === chartId) {
          if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] time.motion.overlay.closed`);
          setActiveMotionType(null);
        }
      })
    );

    // Listen for status response (from active overlays)
    subscriptions.push(
      globalEventStream.subscribe('motion.state.response', (event) => {
        if (event.metadata?.chartId === chartId) {
          const motionType = event.metadata?.motionType as 'planet-drag' | 'time-motion' | null;
          const motionPlanet = event.metadata?.planet as string | null;
          if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] motion.state.response:`, motionType, motionPlanet);
          setActiveMotionType(motionType);
          setActivePlanet(motionPlanet);
          if (motionType === 'planet-drag' && motionPlanet === planet) {
            setIsOverlayOpen(true);
          }
        }
      })
    );

    // Request current state from any active overlay
    if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] Requesting motion state`);
    globalEventStream.emit(createEvent<BaseEvent>(
      'motion.state.query',
      'PlanetMovementSettings',
      { metadata: { chartId } }
    ));

    return () => {
      if (DEBUG) devLog(`[PlanetMovementSettings:${planet}] Unsubscribing`);
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }, [chartId, planet]);

  // Toggle expand
  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <div className="planet-detail-expandable-container">
      <div className="planet-detail-row">
        <ExpandableLabel
          label="Movement"
          isExpanded={isExpanded}
          onToggle={handleToggle}
          ariaControls={`planet-movement-settings-${chartId}-${planet}`}
        />
      </div>

      {isExpanded && (() => {
        // Check if this planet's drag is active
        const isThisActive = activeMotionType === 'planet-drag' && activePlanet === planet;
        // Disabled if: another planet drag is active OR time motion is active
        const isDisabledByOther = (activeMotionType === 'planet-drag' && activePlanet !== planet) ||
                                   activeMotionType === 'time-motion';

        return (
          <div
            id={`planet-movement-settings-${chartId}-${planet}`}
            className="planet-detail-subsection"
            style={{ opacity: isDisabledByOther ? 0.5 : 1 }}
          >
            {/* Step selector + Open button on same line */}
            <HStack spacing={3} align="center" flexWrap="wrap">
              <HStack spacing={2} align="center">
                <Text fontSize="xs" color="gray.500">Step:</Text>
                <StepValueSelector
                  value={stepValue}
                  onChange={handleValueChange}
                  disabled={isDisabledByOther}
                />
              </HStack>

              {/* Active badge or Open button */}
              {isThisActive ? (
                <Badge colorScheme="green" variant="solid" fontSize="xs">
                  Active
                </Badge>
              ) : (
                <MotionOpenButton
                  isActive={isOverlayOpen}
                  disabled={disabled || isDisabledByOther}
                  onClick={handleOpenClick}
                />
              )}
            </HStack>

            {/* Range info */}
            <Text fontSize="2xs" color="gray.400" mt={1}>
              Range: ±{formatRange(stepValue)}
            </Text>
          </div>
        );
      })()}
    </div>
  );
};

export default PlanetMovementSettings;
